import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Menu, X, Globe } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useLang } from '../../contexts/LangContext'

export default function TopNav() {
  const { currentUser, signOut } = useAuth()
  const { locale, setLocale, t } = useLang()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  function handleSignOut() {
    signOut()
    setUserMenuOpen(false)
    navigate('/')
  }

  const isAdmin = currentUser?.admin === true

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-bold text-xl text-primary-600">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0">
            <rect width="28" height="28" rx="8" fill="#2563eb" />
            <path d="M7 14c0-3.866 3.134-7 7-7s7 3.134 7 7-3.134 7-7 7-7-3.134-7-7z" fill="white" fillOpacity=".25" />
            <path d="M11 13l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Helply
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
          {!currentUser && (
            <>
              <Link to="/" className="hover:text-primary-600 transition-colors">
                {t('Αρχική', 'Home')}
              </Link>
              <Link to="/how-it-works" className="hover:text-primary-600 transition-colors">
                {t('Πώς λειτουργεί', 'How it works')}
              </Link>
            </>
          )}
          {isAdmin && (
            <>
              <Link to="/admin" className="hover:text-primary-600 transition-colors">
                {t('Dashboard διαχείρισης', 'Admin dashboard')}
              </Link>
              <Link to="/admin/providers" className="hover:text-primary-600 transition-colors">
                {t('Πάροχοι', 'Providers')}
              </Link>
              <Link to="/admin/bookings" className="hover:text-primary-600 transition-colors">
                {t('Κρατήσεις', 'Bookings')}
              </Link>
            </>
          )}
          {!isAdmin && currentUser?.role === 'customer' && (
            <>
              <Link to="/dashboard" className="hover:text-primary-600 transition-colors">
                {t('Dashboard', 'Dashboard')}
              </Link>
              <Link to="/book" className="hover:text-primary-600 transition-colors">
                {t('Κράτηση', 'Book')}
              </Link>
              <Link to="/bookings" className="hover:text-primary-600 transition-colors">
                {t('Κρατήσεις μου', 'My Bookings')}
              </Link>
              <Link to="/properties" className="hover:text-primary-600 transition-colors">
                {t('Ακίνητα', 'Properties')}
              </Link>
            </>
          )}
          {!isAdmin && currentUser?.role === 'provider' && (
            <>
              <Link to="/provider/dashboard" className="hover:text-primary-600 transition-colors">
                {t('Dashboard', 'Dashboard')}
              </Link>
              <Link to="/provider/bookings" className="hover:text-primary-600 transition-colors">
                {t('Κρατήσεις', 'Bookings')}
              </Link>
              <Link to="/provider/profile" className="hover:text-primary-600 transition-colors">
                {t('Προφίλ', 'Profile')}
              </Link>
            </>
          )}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Lang toggle */}
          <button
            onClick={() => setLocale(locale === 'el' ? 'en' : 'el')}
            className="hidden md:flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-primary-600 transition-colors px-2 py-1 rounded-md hover:bg-slate-100"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            {locale.toUpperCase()}
          </button>

          {currentUser ? (
            <div className="relative hidden md:block">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                data-cy="nav-current-user"
                className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-primary-600 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100"
              >
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                    {currentUser.firstName[0]}{currentUser.lastName[0]}
                  </span>
                )}
                <span>{currentUser.firstName}</span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {t('Το προφίλ μου', 'My Profile')}
                  </Link>
                  <hr className="my-1 border-slate-100" />
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    {t('Αποσύνδεση', 'Sign Out')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/signin"
                data-cy="nav-signin"
                className="text-sm font-medium text-slate-700 hover:text-primary-600 transition-colors px-3 py-2"
              >
                {t('Σύνδεση', 'Sign In')}
              </Link>
              <Link
                to="/signup"
                data-cy="nav-signup"
                className="text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors px-4 py-2 rounded-lg"
              >
                {t('Εγγραφή', 'Sign Up')}
              </Link>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
          {!currentUser && (
            <>
              <Link to="/" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-slate-700 rounded-lg hover:bg-slate-50">
                {t('Αρχική', 'Home')}
              </Link>
              <Link to="/how-it-works" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-slate-700 rounded-lg hover:bg-slate-50">
                {t('Πώς λειτουργεί', 'How it works')}
              </Link>
              <div className="pt-2 flex gap-2">
                <Link to="/signin" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg">
                  {t('Σύνδεση', 'Sign In')}
                </Link>
                <Link to="/signup" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg">
                  {t('Εγγραφή', 'Sign Up')}
                </Link>
              </div>
            </>
          )}
          {currentUser && (
            <>
              <div className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-700">
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                    {currentUser.firstName[0]}{currentUser.lastName[0]}
                  </span>
                )}
                <span>{currentUser.fullName}</span>
              </div>
              {isAdmin && (
                <>
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                    {t('Dashboard διαχείρισης', 'Admin dashboard')}
                  </Link>
                  <Link to="/admin/providers" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                    {t('Πάροχοι', 'Providers')}
                  </Link>
                  <Link to="/admin/bookings" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                    {t('Κρατήσεις', 'Bookings')}
                  </Link>
                </>
              )}
              {!isAdmin && currentUser.role === 'provider' && (
                <Link to="/provider/dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                  {t('Dashboard', 'Dashboard')}
                </Link>
              )}
              {!isAdmin && currentUser.role === 'customer' && (
                <>
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                    {t('Dashboard', 'Dashboard')}
                  </Link>
                  <Link to="/bookings" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                    {t('Κρατήσεις μου', 'My Bookings')}
                  </Link>
                  <Link to="/properties" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                    {t('Ακίνητα', 'Properties')}
                  </Link>
                </>
              )}
              <Link to="/settings" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-slate-700 rounded-lg hover:bg-slate-50">
                {t('Το προφίλ μου', 'My Profile')}
              </Link>
              <button
                onClick={() => { handleSignOut(); setMobileOpen(false) }}
                className="block w-full text-left px-3 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50"
              >
                {t('Αποσύνδεση', 'Sign Out')}
              </button>
            </>
          )}
          <div className="pt-2 border-t border-slate-100">
            <button
              onClick={() => setLocale(locale === 'el' ? 'en' : 'el')}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 rounded-lg hover:bg-slate-50"
            >
              <Globe size={14} />
              {locale === 'el' ? 'English' : 'Ελληνικά'}
            </button>
          </div>
        </div>
      )}
    </header>
  )
}
